import type { FetchSoapNotesDto, SoapImage, SoapNoteApiItem } from './procedure-api.type'

// In-flight uploads by temp image id — decides which optimistic images survive refetches.
const inFlightUploads = new Map<number, string>()

export const getSoapNotesScopeKey = ({ patientPid, encounterUid }: FetchSoapNotesDto): string =>
  `${patientPid}:${encounterUid}`

export const registerPendingSoapImageUpload = (tempImageId: number, scopeKey: string): void => {
  inFlightUploads.set(tempImageId, scopeKey)
}

export const unregisterPendingSoapImageUpload = (tempImageId: number): void => {
  inFlightUploads.delete(tempImageId)
}

export const isSoapImageUploadInFlight = (tempImageId: number): boolean =>
  inFlightUploads.has(tempImageId)

export const getPendingSoapImageUploadCount = (scopeKey: string): number => {
  let count = 0
  inFlightUploads.forEach((uploadScopeKey) => {
    if (uploadScopeKey === scopeKey) {
      count += 1
    }
  })
  return count
}

export const resetSoapImageUploadRegistry = (): void => {
  inFlightUploads.clear()
}

export const isPendingSoapImage = (image: SoapImage): boolean => image.id < 0

export const hasPendingSoapImages = (notes: SoapNoteApiItem[] | undefined): boolean =>
  Boolean(notes?.some((note) => note.soap_images?.some(isPendingSoapImage)))

// Keeps only optimistic images whose upload is still in flight.
export const filterStillUploadingSoapImages = (images: SoapImage[]): SoapImage[] =>
  images.filter((image) => isPendingSoapImage(image) && isSoapImageUploadInFlight(image.id))

const collectStillUploadingSoapImages = (notes: SoapNoteApiItem[]): SoapImage[] =>
  filterStillUploadingSoapImages(notes.flatMap((note) => note.soap_images || [])).map((image) => ({
    id: image.id,
    image: image.image,
    procedure_id: image.procedure_id,
  }))

// fetchSoapNotes merge(): server data wins, but images still uploading are re-attached.
export const mergeSoapNotesPreservingPendingImages = (
  currentCacheData: SoapNoteApiItem[],
  responseData: SoapNoteApiItem[],
): SoapNoteApiItem[] => {
  const stillUploading = collectStillUploadingSoapImages(currentCacheData)

  if (stillUploading.length === 0) {
    return responseData
  }

  if (responseData.length === 0) {
    return [{ ...currentCacheData[0], soap_images: stillUploading }]
  }

  const [firstNote, ...restNotes] = responseData

  return [
    { ...firstNote, soap_images: [...(firstNote.soap_images || []), ...stillUploading] },
    ...restNotes,
  ]
}
